/**
 * Data Access Layer for Happening Role Requirements
 *
 * Authoritative per-happening staffing need (how many of each role). Distinct
 * from happening_requirements, which tracks things. count = 0 means a role is
 * explicitly not needed (e.g. soundcheck needs no stage manager).
 */

import { getClient } from './client.js';
import { TABLES } from './tables.js';
import type {
  HappeningRoleRequirement,
  HappeningRoleRequirementInput,
} from './types.js';

export async function listHappeningRoleRequirements(
  happeningId: string,
): Promise<HappeningRoleRequirement[]> {
  const { data, error } = await getClient()
    .from(TABLES.happening_role_requirements)
    .select('*')
    .eq('happening_id', happeningId);

  if (error) throw error;
  return (data || []) as HappeningRoleRequirement[];
}

export async function listHappeningRoleRequirementsBatch(
  happeningIds: string[],
): Promise<Record<string, HappeningRoleRequirement[]>> {
  if (happeningIds.length === 0) return {};

  const { data, error } = await getClient()
    .from(TABLES.happening_role_requirements)
    .select('*')
    .in('happening_id', happeningIds);

  if (error) throw error;

  const byHappening: Record<string, HappeningRoleRequirement[]> = {};
  for (const row of (data || []) as HappeningRoleRequirement[]) {
    (byHappening[row.happening_id] ??= []).push(row);
  }
  return byHappening;
}

/** Replace a happening's full set of role requirements in one go. */
export async function setHappeningRoleRequirements(
  happeningId: string,
  requirements: HappeningRoleRequirementInput[],
): Promise<void> {
  const client = getClient();

  const { error: deleteError } = await client
    .from(TABLES.happening_role_requirements)
    .delete()
    .eq('happening_id', happeningId);
  if (deleteError) throw deleteError;

  if (requirements.length > 0) {
    const rows = requirements.map((r) => ({
      happening_id: happeningId,
      role_id: r.role_id,
      count: r.count,
    }));
    const { error: insertError } = await client
      .from(TABLES.happening_role_requirements)
      .insert(rows);
    if (insertError) throw insertError;
  }
}
